import { TVitalsConfigData } from "apis/src/types/tryst";
import {
  ABHA_STATUS,
  APPOINTMENT_CHANNEL,
  APPOINTMENT_STATUS,
  FIELD_SUB_TYPE,
  FIELD_TYPE,
  FOLLOWUP_MESSAGE_PURPOSE,
  MODES_OF_APT,
  PROFILE_PERSONA,
  QUEUE_CONFIG_ACTION_ID,
  QUEUE_CONFIG_COLUMN_ID,
  SupportedTeleProviders,
  TYPES_OF_APT,
} from "../enums/appointment";
import { STAFF_RESTRICTION } from "../enums/conifg";
import { QUEUE_TYPE } from "../enums/queue";
import { PatientProfile } from "./general";
import { MedicalHistoryConfig } from "./genie-profile";
import {
  HealthPackageItem,
  PackageDetailsByPatientId,
} from "./package-booking";
import { ParchiPatient } from "./parchi";
import { TReceipt, TReceiptCounter, TSkuItem } from "./receipt";
import { TWorkflowView } from "./workflow-view";

export type AptPatientProfile = PatientProfile & { amount_pending?: number };

export type Abha = {
  id: string;
  records: number;
  status: ABHA_STATUS;
};

export type VendorMetaItem = {
  id: string;
  name: string;
  value: string;
  type: VendorMetaType;
};

export enum VendorMetaType {
  TEXT = "TX",
  LINK = "LK",
}

export interface Appointment {
  date: string;
  doctor_oid: string;
  archive: boolean;
  aid: string;
  label?: string;
  business_id?: string;
  patient_uuid?: string;
  patient_oid: string;
  clinic_id: string;
  channel?: APPOINTMENT_CHANNEL;
  onsched_id?: string;
  status: APPOINTMENT_STATUS;
  full_date: string;
  start_time: string;
  end_time: string;
  visit_start?: string | Date;
  visit_end?: string | Date;
  created_at?: string;
  payment_id?: string | null;
  payment_status?: string | null;
  sa_id?: string;
  sa_cmpl?: string[];
  /**
   * @deprecated Use sa_cmpl
   */
  assessments?: Record<string, string[]>;
  tele_id?: string;
  stage: string;
  amount?: number;
  prescription_id?: string;
  prescription_url?: string;
  follow_up?: string | Date;
  callback_requested?: string | Date;
  park_allowed?: string | Date;
  updated_at?: string;
  call_status?: string;
  patient_tele_status?: string;
  doctor_tele_status?: string;
  flavour?: string;
  attribute?: string;
  service?: AptServiceConfRecord;
  referral_id?: string;
  last_visit_time?: string | Date;
  visit_count?: number;
  service_type?: number;
  service_conf_id?: string;
  mode?: MODES_OF_APT;
  visit_type?: TYPES_OF_APT;
  doc_dwr_link?: string;
  visit_checkin?: string | Date;
  phr_id?: string;
  dwr_link?: string;
  patient_profile?: string;
  tags?: Array<number | string>;
  patient: AptPatientProfile;
  formData: Array<TFormData>;
  abha?: Abha;
  "visit-type": "APPOINTMENT" | "WALKIN" | "TELE";
  comm_meta: CommunicationMetaItem[] | null;
  receipts: Array<TReceipt>;
  token?: number;
  "type-display": string;
  billStatus: "paid" | "unbilled" | "pending" | "refunded" | "failed";
  billTotal?: number;
  discount_conf?: AppointmentDiscountDetail;
  package_inclusions?: PackageInclusion[];
  vendor_meta: VendorMetaItem[];
  treatmentPlans?: TTreatmentPlan[];
  apt_fields?: TAppointmentField[];
  vc_meta: QueueVcMeta | null;
  rc?: boolean;
  can_abort?: boolean;
  can_pay?: boolean;
  chips?: AppointmentChips[];
  print_prescription_url?: string;
  uhid?: string;
  inclusion_ids?: number[];
  new_patient?: boolean;
}

export type AppointmentChip<ChipType, ChipData> = {
  type: ChipType;
  icon: AppointmentChipIcon;
  data: ChipData;
  cta: string;
};

export enum AppointmentChipIcon {
  INFO_ICON = "info",
}

export enum AppointmentChipType {
  PACKAGES = "PACKAGES",
  DENTAL_PACKAGES = "DENTAL_PACKAGES",
}

export type AppointmentChips =
  | AppointmentChip<AppointmentChipType.PACKAGES, undefined>
  | AppointmentChip<AppointmentChipType.DENTAL_PACKAGES, undefined>;

export type QueueVcMeta = {
  platform: SupportedTeleProviders;
  host_link?: string;
  meet_link?: string;
};

export type TAppointmentField = {
  field_id: string;
  values: Array<string>;
};

export type TTreatmentPlan = {
  plan_id: string;
  name: string;
};

export interface PackageInclusion {
  inclusion_id: number;
  inclusion_order: number;
  name: string;
  package_transaction: PackageTransaction;
}
export interface PackageTransaction {
  package_transaction_id: number;
  package_name: string;
  total_price: number;
}

export type AllQueuesData = {
  [key: string]: Appointment[];
  followupAppointments: Appointment[];
  bookedAppointments: Appointment[];
  otherAppointments: Appointment[];
  requestedAppointments: Appointment[];
};

export type AllBusinessQueueData = AllQueuesData;

export type AppointmentDiscountDetail = {
  coupon_code: string;
  discount_amount: number;
  discount_type: string;
  discount: number;
  is_cod_allowed: boolean;
  user_topic: string;
};

export interface AptServiceConfRecord {
  owner_id: string; // primary key in main index 161419272566611
  conf_id: string; // service_id-duration-price
  service_id: number; // CONS
  duration: number; // 15
  price: number; // 400
  eka_fee: number; // Minimum of (flat fee, %of price) Rule engine to be der in vagus
  conv_fee: number;
  currency: "INR";
  post_pay: boolean;
  pre_pay: boolean;
  archive: boolean;
  service_name: string;
  book_ahead_days: number;
  allow_patient_booking: boolean;
  nickname?: string;
  followup_validity: null | number;
  appointment_type: TYPES_OF_APT;
  mode: MODES_OF_APT[];
  c_at: string;
}

export type TFormData = {
  label:
  | "UHID"
  | "Tag"
  | "Patient Address"
  | "Pincode"
  | "Mail ID"
  | "Blood Group"
  | "Salutation"
  | "City"
  | "Alternative Phone"
  | "Referred By"
  | "Referred Doctor's Number"
  | "Marital Status"
  | "Name of Informant"
  | "Channel"
  | "Patient's Occupation";
  type: "string";
  key:
  | "uhid"
  | "tag"
  | "paddress"
  | "pincode"
  | "email"
  | "bloodgroup"
  | "salutation"
  | "city"
  | "aphone"
  | "referredby"
  | "referredbynumber"
  | "maritalstatus"
  | "noi"
  | "channel"
  | "pocc";
  value: string;
};

export type CommunicationMetaItem = {
  purpose: FOLLOWUP_MESSAGE_PURPOSE;
  type: "Scheduled" | "Immediate";
  status:
  | "Scheduled"
  | "Sent"
  | "Responded"
  | "Cancelled"
  | "Delivered"
  | "Read"
  | "Clicked";
  messageResponse?: "Yes" | "No" | string;
  messageId?: string; //Messenger Message Id for immediate stuff and some unique key combination with aid + index in comm_meta array
  sentAt?: string; //ISO Datetime
  schFor?: string;
};

export type PatientSearchAppointment = Pick<
  Appointment,
  | "aid"
  | "status"
  | "date"
  | "comm_meta"
  | "doctor_oid"
  | "start_time"
  | "tags"
  | "visit_type"
  | "clinic_id"
  | "prescription_id"
  | "prescription_url"
  | "receipts"
  | "token"
  | "visit_checkin"
  | "visit_start"
  | "visit_end"
> & { billed_amount: number; is_billed: boolean };

export type PatientOverview = Pick<Appointment, "visit_count"> & {
  patient: PatientProfile;
  last_visit: Appointment;
  upcoming_appointment: Appointment;
  dues_pending: number;
  package?: PackageDetailsByPatientId;
};

export interface TagRecord {
  owner_id: string;
  tag_id: number | string;
  label: string;
  hex: string;
  bg_hex: string;
  archive: boolean;
}

export type TagRecordWithCount = TagRecord & { count: number };

export type QueueConfigItemType = {
  // TODO: check column id type
  columnId: QUEUE_CONFIG_ACTION_ID | QUEUE_CONFIG_COLUMN_ID;
  title: string;
  subtitle: string;
  isSelected: boolean;
};

export type QueueConfigType = {
  actions: QUEUE_CONFIG_ACTION_ID[];
  columns: QUEUE_CONFIG_COLUMN_ID[];
};

export type AppointmentConfigs = {
  tags: TagRecordWithCount[];
  old_tags: TagRecordWithCount[];
  labels: {
    label: string;
    label_id: string;
    hex: string;
    bg_hex: string;
    archive: boolean;
  }[];
  user_preferences: Partial<
    Record<QUEUE_TYPE | `fd_${QUEUE_TYPE}` | string, QueueConfigType>
  >;
  sku_items: Array<TSkuItem>;
  is_edit_receipt_restricted: boolean;
  receipt_counter: TReceiptCounter[];
  packages: HealthPackageItem[];
  apt_config: {
    allow_clinic_booking: boolean;
    pre_consulation_notes: boolean;
    disable_auto_abort_appt: boolean;
    show_vendor_meta?: boolean;
    show_slots_in_schedule?: boolean;
    allow_pt_multi_apt?: boolean;
    allow_slot_overbooking?: boolean;
    slot_overbooking_limit_per_day?: {
      [docid: string]: string;
    };
  };
  slot_config?: TDoctorsSlotConfig;
  workflow_views?: TWorkflowView[];
  apt_fields?: TAptField[];
  loginmeta?: {
    id?: string;
    role?: string;
    partner_id?: string;
  };
  rtTags?: rtTags[];
};

export type rtTags = {
  value: string;
  label: string;
  hex?: string;
  bg_hex?: string;
  archive?: boolean;
};
export type CreateTagBody = Omit<TagRecord, "tag_id" | "archive" | "owner_id">;

export type TBillingQueue = {
  apt: Array<Appointment>;
  countResponse?: TCountResponseObject;
};

export interface TCount {
  count: number;
}

export interface TCountResponseObject {
  completed: TCount;
  upcoming: TCount;
  other: TCount;
  today?: TCount & { saCount: number };
}

export type CustomSlot = {
  s: string;
  e: string;
  available: boolean;
};

export type SlotTime = { startTime: string; endTime: string };

export interface ClinicServicesAndSchedule {
  services: Services;
}

export type Services = Record<string, ServiceConf>;

export type ServiceConf = {
  allow_patient_booking: boolean;
  appointment_type: string;
  archive: boolean;
  book_ahead_days: number;
  c_at: string;
  conf_id: string;
  conv_fee: number;
  currency: string;
  duration: number;
  eka_fee?: null;
  followup_validity?: null;
  mode?: string[] | null;
  nickname: string;
  owner_id: string;
  post_pay: boolean;
  pre_pay: boolean;
  price?: null;
  service_id: number;
  service_name: string;
};

export type TGetSlotParams = {
  startDate: string;
  endDate: string;
  doctorId: string;
  clinicId?: string;
  mode?: string;
  serviceId?: string;
  appointment_type?: string;
  last_appointment_date?: string;
};

export type TBookSlotPayload = {
  start_time: Date | string;
  end_time: Date | string;
  clinic_id: string;
  patient_oid: string;
  action?:
  | APPOINTMENT_STATUS.BOOKED
  | APPOINTMENT_STATUS.CHECKED_IN
  | "BOOK_AND_CHECK_IN";
  channel:
  | APPOINTMENT_CHANNEL.DOCTOR
  | APPOINTMENT_CHANNEL.STAFF
  | APPOINTMENT_CHANNEL.WALKIN;
  tags?: Array<number>;
  mode: MODES_OF_APT;
};

export type TBookSlotPayloadForBusiness = {
  start_time: Date | string;
  end_time: Date | string;
  clinic_id: string;
  mode: MODES_OF_APT;
  action?:
  | APPOINTMENT_STATUS.BOOKED
  | APPOINTMENT_STATUS.CHECKED_IN
  | "BOOK_AND_CHECK_IN";
  channel:
  | APPOINTMENT_CHANNEL.DOCTOR
  | APPOINTMENT_CHANNEL.STAFF
  | APPOINTMENT_CHANNEL.WALKIN;
  tags?: Array<TagRecord["tag_id"]>;
  patient: TCreateAptPatient | { oid: string };
  packages?: {
    inclusion_ids?: Array<number>;
  };
  allow_overlap?: boolean;
  vc_meta?: {
    platform: SupportedTeleProviders;
    meet_link?: string;
    host_link?: string;
  };
};

export interface DayPaymentSummaryEntity {
  date: string;
  no_of_visits: number;
  no_of_completed_visits: number;
  no_of_patients: number;
  no_of_other_visits: number;
  no_of_paid_visits: number;
  total_payment: number;
  total_payment_before_discounts: number;
  total_discounts: number;
  total_dues: number;
  total_settled_dues: number;
  payments_split: {
    cash: number;
    credit_card: number;
    credit: number;
    debit_card: number;
    eka_prepaid: number;
    eka_postpaid: number;
    gpay: number;
    other: number;
    paytm: number;
    upi: number;
  };
  service_split: {
    amount: number;
    service_name: string;
    receipt_discount_on_sku: number;
  }[];
}

export interface TSettlementReportDataItem {
  aid: string;
  date: string;
  doctor_name: string;
  patient_name: string;
  transfer_amount: string;
  payout_status: string;
  payout_mode: string;
  payout_utr: string;
  payout_created_at: string;
  payout_purpose: string;
}

export interface SummaryReportDataItemType {
  title: string;
  data: Record<string, string | number>;
}

export type TPostQueueConfigUserPreference = {
  preferenceFor: keyof AppointmentConfigs["user_preferences"];
  preferences: QueueConfigType;
};

export type RescheduleAppointment = {
  appointment: Omit<Appointment, "patient">;
};

export type ReschedulePayload = Pick<
  Appointment,
  "aid" | "start_time" | "end_time" | "mode"
> & {
  meta: APPOINTMENT_CHANNEL.DOCTOR | APPOINTMENT_CHANNEL.STAFF;
} & Partial<Pick<Appointment, "clinic_id">> & {
  doctor_to_change_to_oid?: string;
};

export type TTagToggleAction = "ADDED" | "DELETED" | "MULTI";

export type CreateAppointmentTagProps = {
  docId: string;
  tagRecord: CreateTagBody;
  aid?: string;
};

export interface BusinessAppointmentConfigs extends AppointmentConfigs {
  doctors: DoctorsInBusiness[];
  clinics: ClinicsInBusiness[];
  doctors_config: GetBulkConfig[];
  static_assets: QueueStaticAssets;
}

export interface QueueStaticAssets {
  links: StaticLink[];
}

export interface StaticLink {
  title: string;
  link: string;
}
export interface GetBulkConfig {
  version: number;
  id: string;
  staffRestriction?: STAFF_RESTRICTION[];
  medicalHistoryConfig?: MedicalHistoryConfig | null;
  vitalsConfig: TVitalsConfigData["selectedItems"];
  inventory?: null;
}

export interface ClinicsInBusiness {
  est_id: string;
  name: string;
  id: number;
  doctors: string[];
  hip_id?: string;
}

export interface DoctorsInBusiness {
  name: string;
  oid: string;
  discovery_id?: number;
  pic: string;
  username: string;
  hpr_id?: string;
}

export type TBusiness = {
  business_id: string;
  name: string;
  logo?: string;
  about?: string;
};

export interface JwtPayloadType {
  oid?: string;
  "doc-id"?: string;
  uuid: string;
  type?: 1 | 2 | 3;
  mob?: PatientProfile["mobile"];
  fn?: PatientProfile["fn"];
  fln?: PatientProfile["fln"];
}

export interface TInitUploadMedicalRecordArgs {
  dt: string;
  sh: boolean;
  tg?: string[] | null;
  files: TInitUploadMedicalRecordFilesEntity[];
  dd: string;
}

export interface TInitUploadMedicalRecordFilesEntity {
  contentType: string;
}

export interface TInitUploadDocsResponse {
  error: boolean;
  document_id: string;
  forms?: TInitUploadFormsEntity[] | null;
  message?: string;
}
export interface TInitUploadFormsEntity {
  url: string;
  fields: TInitUploadFields;
}
export interface TInitUploadFields {
  key: string;
  bucket: string;
  "X-Amz-Algorithm": string;
  "X-Amz-Credential": string;
  "X-Amz-Date": string;
  "X-Amz-Security-Token": string;
  Policy: string;
  "X-Amz-Signature": string;
}

export type TCreateAptPatient = Pick<
  PatientProfile,
  "fln" | "dob" | "gen" | "mobile"
> & {
  formData: ParchiPatient["formData"];
  oid?: string;
  abha: ParchiPatient["abha"];
};

export type TActivityLogStyles = {
  title: string;
  actor: string;
  actorName: string;
  titleStyle: string;
  titleFont: string;
  actorFont: string;
  actorNameFont: string;
  actionIcon: TActivityIcon;
  dayByMonth: string;
  time: string;
  actor_role: "doctor" | "staff";
};

export type TActivityIcon = {
  iconType: "far" | "fas";
  iconName:
  | "tag"
  | "circle-check"
  | "calendar"
  | "archive"
  | "circle-exclamation"
  | "person-walking-dashed-line-arrow-right"
  | "stethoscope"
  | "repeat"
  | "circle-xmark"
  | "message-pen";
};

export type AptChannel = "BOOKED" | "FOLLOW-UP" | "REQUESTED";

export type BookType = "SCHEDULE" | "RESCHEDULE";

export type TDoctorsSlotConfig = Record<
  string,
  {
    durations: number[];
  }
>;

export type TAptField = {
  field_id: string;
  options?: Array<TAptFieldOption>;
  fieldMeta: {
    type: FIELD_TYPE;
    subType: FIELD_SUB_TYPE;
  };
  title: string;
  is_editable?: boolean;
};

export type TAptFieldOption = {
  id: string;
  value: string;
  description?: string;
  color?: string;
  bgColor?: string;
  isSelected?: boolean;
};

export type TBusinessUser = {
  fln: string;
  oid: string;
  persona: PROFILE_PERSONA;
  archive?: boolean;
};
